import { scrollIntoView } from "@app/utils/scrollIntoView"
import { getProductInventoryByStyleColourCode } from "@app/lib/product"
import { ProductInventory } from "@app/types/product"
import { EVENT_QUANTITY_INPUT_CHANGE } from "@app/components/quantity-input"
import { getZCodeCartQuantity } from "@app/utils/z-codes"
import { EVENT_CART_UPDATE } from "@app/components/cart"

export class SizeButtons extends HTMLElement {
  $idInput: HTMLInputElement;
  $buttons: NodeListOf<HTMLButtonElement>;
  $activeButton: HTMLButtonElement | null;
  $zCodeMessage: HTMLDivElement;
  $error: HTMLDivElement;
  $label: HTMLDivElement;
  $selectedLabel: HTMLDivElement;
  $sizeMap: { [key: string]: string };
  $selectedQuantity: number;

  constructor() {
    super();
    this.$idInput = this.querySelector("input[name='id']")!;
    this.$buttons = this.querySelectorAll(".size-button");
    this.$activeButton = null;
    this.$zCodeMessage = this.querySelector(".size-buttons--message")!;
    this.$error = this.querySelector(".size-buttons--error")!;
    this.$label = this.querySelector(".size-buttons--label")!;
    this.$selectedLabel = this.querySelector(".size-buttons--label-selected")!;
    this.$sizeMap = {}
    this.$selectedQuantity = 1

    if (this.hasAttribute("has-z-code") && !!this.dataset.zStyleCode && !!this.dataset.zColourCode) {
      this.initSizeMap()
      this.initZCodeSizes()

      document.addEventListener(EVENT_CART_UPDATE, () => {
        this.updateZCodeDisclaimer()
      })

      document.addEventListener(EVENT_QUANTITY_INPUT_CHANGE, (e: any) => {
        this.$selectedQuantity = e.detail?.quantity || 1
        this.updateZCodeDisclaimer()
      })
    }
  }

  connectedCallback() {
    this.$buttons.forEach((button) => {
      button.addEventListener("click", () => {
        const id = button.dataset.id || "";
        const text = button.dataset.text || "";
        this.$idInput.value = id;
        this.updateSelectedLabel(text);
        this.$idInput.dispatchEvent(new Event("change"));
        this.$buttons.forEach((b) => b.classList.remove("is-active"));
        button.classList.add("is-active");
        this.$activeButton = button
        this.updateZCodeDisclaimer()
        this.resetError();
      });
    });

    if (this.dataset.initialVariant) {
      this.updateSelectedLabel();
    }
  }

  initSizeMap() {
    if (!this.dataset.zSizeMapping) {
      return
    }

    const split = this.dataset.zSizeMapping.split(",")
    for (const item of split) {
      const itemSplit = item.split(":")
      if (itemSplit.length === 2) {
        this.$sizeMap[itemSplit[0]] = itemSplit[1]
      }
    }
  }

  async initZCodeSizes() {
    const zProduct = await getProductInventoryByStyleColourCode(this.dataset.zStyleCode!, this.dataset.zColourCode!)

    this.$buttons.forEach((button) => {
      if (!button.dataset.text) {
        return
      }

      const mappedSize = this.getMappedSize(button.dataset.text, zProduct)
      const quantity = Number(button.dataset.quantity) || 0
      const zQuantity = mappedSize?.quantityAvailable || 0

      // Store Z-code ID and inventory on button dataset
      const zId = mappedSize?.id.split("/").pop()
      if (zId) {
        button.dataset.zId = zId
      }
      button.dataset.zText = mappedSize?.selectedOptions.find(option => option.name === "Size")?.value.toString()
      button.dataset.zQuantity = zQuantity.toString()

      // Update out of stock status based on z-code inventory
      const totalQuantity = quantity + zQuantity
      if (totalQuantity > 0) {
        button.removeAttribute("disabled")
        button.classList.remove("size-button--out-of-stock")
      } else {
        button.setAttribute("disabled", "true")
        button.classList.add("size-button--out-of-stock")
      }
    })

    this.showSizeButtonsWrapper()
  }

  getMappedSize = (currentSizeValue: string, zProduct: ProductInventory) => {
    if (!zProduct.variants?.nodes) {
      return null
    }

    // Infer direct 1:1 mapping if mapping not explicitly defined
    const mappedSizeValue = this.$sizeMap?.[currentSizeValue] || currentSizeValue

    return zProduct.variants?.nodes.find(node =>
      node.selectedOptions.find(option => option.name === "Size")?.value === mappedSizeValue
    )
  }

  updateZCodeDisclaimer() {
    if (!this.$activeButton?.dataset.zId || this.$activeButton?.dataset.text === this.$activeButton?.dataset.zText) {
      this.$zCodeMessage.innerText = ""
      return
    }

    const zQuantityInCart = getZCodeCartQuantity(this.$activeButton?.dataset.zId)
    const zQuantityAvailable = Math.max(parseInt(this.$activeButton?.dataset.zQuantity || "0") - zQuantityInCart, 0)
    const zQuantityToAdd = Math.min(this.$selectedQuantity, zQuantityAvailable)

    if (zQuantityToAdd) {
      this.$zCodeMessage.innerText = this?.dataset?.zDisclaimer
        ?.replace("[z-quantity]", zQuantityToAdd.toString())
        ?.replace("[z-size]", this.$activeButton?.dataset.zText || "") || ""
    } else {
      this.$zCodeMessage.innerText = ""
    }
  }

  showSizeButtonsWrapper() {
    const sizeButtonsWrapper = this.querySelector(".size-buttons--btn-wrapper")
    sizeButtonsWrapper?.classList.remove("visibility-hidden")
  }

  showError() {
    this.classList.add("error");
    this.$error.innerText = "Please select a size";
    scrollIntoView(this);
  }

  resetError() {
    this.classList.remove("error");
  }

  updateSelectedLabel(size?: string) {
    this.$label.classList.add("hidden");
    this.$selectedLabel.classList.remove("hidden");
    if (size) (this.$selectedLabel.querySelector(".size-selected-text") as any).innerText = size;
  }
}

customElements.define("size-buttons", SizeButtons);
